import { forwardRef, LegacyRef, ReactNode } from "react";
import clsx from "clsx";
import ErrorBoundary from "../error-boundary";
import { NavLink, Link as ReactLink } from "react-router-dom";

import styles from "./link.module.scss";
import { AuroraComponentProps } from "../auroraComponentProps";

export const types = {
  a: "a",
  button: "button",
  navLink: NavLink,
  link: ReactLink,
};

type Types = typeof types;

type LinkProps = {
  /** The target url for the link, or callback function if the type is button */
  to: string;
  /** What type of link to use, ignored if 'to' prop is defined */
  type?: keyof Types;
  /** Removes underline and :visited, typical for links style */
  noLinkStyling?: boolean;
  /** Optional extra class(es) */
  className?: string;
  children: ReactNode;
};

type LinkType =
  | HTMLAnchorElement
  | HTMLButtonElement
  | typeof NavLink
  | typeof ReactLink;

const _Link = forwardRef<
  LinkType,
  AuroraComponentProps<LinkProps, "a" | "button">
>(
  (
    {
      to,
      type = "link",
      className,
      children,
      onFocus,
      onBlur,
      noLinkStyling = false,
      ...rest
    },
    ref
  ) => {
    let Tag = types[type];
    const classes = [{ [styles["link"]]: !noLinkStyling }, className];

    if (
      to &&
      typeof to === "string" &&
      to.match(/^(\/\/)|(https?:\/\/)|(file:\/\/)|(mailto:)|(tel:)|(#)/)
    ) {
      // This is an absolute url
      Tag = types.a;
      return (
        <ErrorBoundary>
          <a
            {...(rest as any)}
            ref={ref as LegacyRef<HTMLAnchorElement>}
            className={clsx(classes)}
            href={to}
            onFocus={onFocus}
            onBlur={onBlur}
          >
            {children}
          </a>
        </ErrorBoundary>
      );
    }

    switch (type) {
      case types.button:
        return (
          <ErrorBoundary>
            <button
              type="button"
              {...(rest as any)}
              ref={ref as LegacyRef<HTMLButtonElement>}
              className={clsx(classes, styles.linkButton)}
              onClick={to as unknown as () => void}
              onFocus={onFocus}
              onBlur={onBlur}
            >
              {children}
            </button>
          </ErrorBoundary>
        );
      case types.a:
        return (
          <a
            {...(rest as any)}
            ref={ref as LegacyRef<HTMLAnchorElement>}
            className={clsx(classes)}
            href={to}
            onFocus={onFocus}
            onBlur={onBlur}
          >
            {children}
          </a>
        );
      default:
        return (
          <ErrorBoundary>
            <Tag
              {...(rest as any)}
              ref={ref as LegacyRef<any>}
              className={clsx(classes)}
              to={to}
              onFocus={onFocus}
              onBlur={onBlur}
            >
              {children}
            </Tag>
          </ErrorBoundary>
        );
    }
  }
);

const Link = Object.assign(_Link, {
  types: Object.keys(types).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<Types>,
});

Link.displayName = "Link";

export default Link;
